import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { MonkeyLogo } from '../components/MonkeyLogo';
import { api } from '../utils/api';
import { useAuth } from '../App';

const STATES = { LOADING: 'loading', SUCCESS: 'success', ERROR: 'error' };

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth() || {};
  const token = params.get('token') || '';
  const [state, setState] = useState(STATES.LOADING);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setState(STATES.ERROR);
      setMessage('Missing verification token. Open the link from your inbox.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.verifyEmail(token);
        if (cancelled) return;
        setState(STATES.SUCCESS);
        setMessage(r?.message || 'Email verified successfully');
        // Refresh the cached user so the EmailVerifyBanner disappears immediately
        // instead of requiring a manual page refresh.
        if (refreshUser) {
          try { await refreshUser(); } catch (_) { /* ignore */ }
        }
      } catch (e) {
        if (cancelled) return;
        setState(STATES.ERROR);
        setMessage(e?.message || 'Verification failed');
      }
    })();
    return () => { cancelled = true; };
  }, [token, refreshUser]);

  return (
    <div className="min-h-screen bg-[#06070A] text-white relative" data-testid="verify-email-page">
      <Helmet>
        <title>Verify your email — LinkMonkey</title>
      </Helmet>
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[120vw] h-[60vh] pointer-events-none"
           style={{ background: 'radial-gradient(ellipse at top, rgba(0,85,255,0.18) 0%, transparent 60%)' }} />

      <nav className="relative z-10 px-6 md:px-10 py-6 flex items-center max-w-7xl mx-auto">
        <Link to="/" className="flex items-center gap-3">
          <MonkeyLogo size={32} />
          <span className="text-base font-semibold">LinkMonkey</span>
        </Link>
      </nav>

      <main className="relative z-10 max-w-md mx-auto px-6 md:px-0 pt-12 md:pt-24 text-center">
        {state === STATES.LOADING && (
          <div className="flex flex-col items-center gap-5" data-testid="verify-loading">
            <Loader2 className="w-10 h-10 animate-spin text-white/60" />
            <h1 className="text-3xl md:text-4xl font-black tracking-tighter">Verifying your email…</h1>
            <p className="text-white/55 text-[15px] leading-relaxed">This should only take a second.</p>
          </div>
        )}

        {state === STATES.SUCCESS && (
          <div className="flex flex-col items-center gap-5" data-testid="verify-success">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tighter">You're verified 🎉</h1>
            <p className="text-white/60 text-[15px] leading-relaxed max-w-sm">
              {message}. You can now access every part of LinkMonkey.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white text-black text-sm font-semibold hover:bg-white/90 transition-colors"
              data-testid="verify-cta-dashboard"
            >
              Go to dashboard <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {state === STATES.ERROR && (
          <div className="flex flex-col items-center gap-5" data-testid="verify-error">
            <div className="w-16 h-16 rounded-full bg-red-500/15 border border-red-400/30 flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tighter">Couldn't verify</h1>
            <p className="text-white/60 text-[15px] leading-relaxed max-w-sm">{message}</p>
            <p className="text-white/40 text-sm leading-relaxed">
              Log in to your account and request a new verification email from the banner at the top of your dashboard.
            </p>
            <Link
              to="/login"
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white/[0.06] border border-white/10 text-sm font-medium hover:bg-white/[0.1] transition-colors"
              data-testid="verify-cta-login"
            >
              Go to login <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
